import { useEffect, useMemo, useState, useCallback } from 'react';
import { appUrl } from '../lib/appBase.js';
import {
  buildConsoleGroups,
  fetchPublicRomFiles,
  fetchVaultManifest,
  fetchRomCatalog,
  fetchZipIndex,
} from '../lib/romUtils.js';
import { vimmToEjsKey } from '../lib/systems.js';

export function useRomLibrary(searchTerm) {
  const [vaultEntries, setVaultEntries] = useState([]);
  const [publicRomFiles, setPublicRomFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [romCatalog, setRomCatalog] = useState({});
  const [zipIndex, setZipIndex] = useState({});

  const reload = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      fetchVaultManifest(appUrl),
      fetchPublicRomFiles(appUrl),
      fetchRomCatalog(appUrl),
      fetchZipIndex(appUrl),
    ])
      .then(([vault, files, catalog, zips]) => {
        if (cancelled) return;
        const enriched = vault.map((entry) => ({
          ...entry,
          _ejsKey: vimmToEjsKey(entry.system, entry.systemCode),
        }));
        setVaultEntries(enriched);
        setPublicRomFiles((files || []).filter(Boolean));
        setRomCatalog(catalog || {});
        setZipIndex(zips || {});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const groups = useMemo(
    () => buildConsoleGroups(vaultEntries, publicRomFiles, searchTerm, romCatalog, zipIndex),
    [vaultEntries, publicRomFiles, searchTerm, romCatalog, zipIndex],
  );

  const totalGames = useMemo(
    () => groups.reduce((n, g) => n + g.games.length, 0),
    [groups],
  );

  const totalVault = vaultEntries.length;
  const totalFiles = publicRomFiles.length;

  return { groups, loading, totalGames, totalVault, totalFiles, publicRomFiles, refresh: reload };
}
